import { useState } from 'react';
import { Card, Button } from '@atoms/index';
import { orderService } from '@services/orderService';
import type { OrderStatus } from '@/types';
import { formatOrderStatus } from '@utils/formatters';

interface OrderSimulationPanelProps {
  orderId: string;
  currentStatus: OrderStatus;
  onStatusUpdate: () => void;
}

export const OrderSimulationPanel = ({
  orderId,
  currentStatus,
  onStatusUpdate,
}: OrderSimulationPanelProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showDevTools = import.meta.env.DEV || import.meta.env.VITE_SHOW_DEV_TOOLS === 'true';
  if (!showDevTools) {
    return null;
  }

  const updateStatus = async (newStatus: OrderStatus) => {
    setIsLoading(true);
    setError(null);

    try {
      await orderService.updateOrderStatus(orderId, newStatus);
      onStatusUpdate();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Status update failed');
      console.error('Order status update error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="mb-6 border-2 border-yellow-400 bg-yellow-50">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">🧪 DevTools: Sipariş Simülasyonu</h3>
            <p className="text-sm text-gray-600">Mevcut Durum: <strong>{formatOrderStatus(currentStatus)}</strong></p>
          </div>
          <span className="px-2 py-1 bg-yellow-200 text-yellow-800 text-xs font-bold rounded">
            SADECE DEV
          </span>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {currentStatus === 'CREATED' || currentStatus === 'PENDING' ? (
            <Button
              onClick={() => updateStatus('PREPARING')}
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isLoading ? 'Güncelleniyor...' : 'Hazırlanıyor yap'}
            </Button>
          ) : null}

          {currentStatus === 'PREPARING' ? (
            <Button
              onClick={() => updateStatus('READY')}
              disabled={isLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isLoading ? 'Güncelleniyor...' : 'Hazır yap'}
            </Button>
          ) : null}

          {currentStatus === 'READY' ? (
            <Button
              onClick={() => updateStatus('ON_WAY')}
              disabled={isLoading}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {isLoading ? 'Güncelleniyor...' : 'Kurye simülasyonunu başlat (Yolda)'}
            </Button>
          ) : null}

          {currentStatus === 'ON_WAY' ? (
            <Button
              onClick={() => updateStatus('DELIVERED')}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isLoading ? 'Güncelleniyor...' : 'Teslim edildi yap'}
            </Button>
          ) : null}

          {currentStatus === 'DELIVERED' && (
            <p className="text-sm text-gray-600">✅ Sipariş zaten teslim edildi</p>
          )}
        </div>

        <p className="mt-3 text-xs text-gray-500">
          ⚠️ Bu panel yalnızca development modunda görünür. Test amaçlı sipariş durumunu manuel ilerletmenizi sağlar.
        </p>
      </div>
    </Card>
  );
};
